import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  LayoutDashboard,
  Pencil,
  Receipt,
  Route,
  ShieldCheck,
  ShoppingBag,
  Trash2,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { Button, FormModal } from "@veorest/ui";
import {
  deleteAdminClient,
  fetchAdminClient,
  fetchAdminPartners,
  updateAdminClientProfile,
  updateAdminClientStatus,
  updateAdminClientType,
} from "@veorest/api-client";
import { ROUTES } from "../routes";
import { StatsStrip } from "../components/data/StatsStrip";
import { AdminWalletCard } from "../components/wallets/AdminWalletCard";
import { formatDate, formatIqd, formatNumber, statusPillClass } from "../lib/format";
import { clientTypeLabel, clientTypeStatusLabel } from "../lib/platformModel";

type TabKey = "overview" | "billing" | "orders" | "governance";

const TABS: Array<{ key: TabKey; label: string; icon: LucideIcon }> = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "billing", label: "Billing", icon: Receipt },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "governance", label: "Governance", icon: ShieldCheck },
];

function friendlyClientError(err: unknown): string {
  const m = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (m.includes("username_taken")) return "Username already taken — choose another.";
  if (m.includes("username_reserved")) return "That username is reserved.";
  if (m.includes("username_invalid") || m.includes("username_required")) {
    return "Invalid username: 3–30 chars (a-z, 0-9, dash only).";
  }
  if (m.includes("client_not_found")) return "Client not found.";
  return err instanceof Error ? err.message : "Request failed";
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] pb-2">
      <dt className="font-semibold text-[var(--text-muted)]">{label}</dt>
      <dd className="text-right font-bold">{value}</dd>
    </div>
  );
}

export default function ClientDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>("overview");
  const [typeOpen, setTypeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({
    clientType: "oelox" as "oelox" | "partner",
    controllingPartnerId: "",
    reason: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    address: "",
    username: "",
    email: "",
  });

  const detail = useQuery({
    queryKey: ["admin-client", id],
    queryFn: () => fetchAdminClient(id),
    enabled: Boolean(id),
  });

  const partnersQuery = useQuery({
    queryKey: ["admin-partners-select"],
    queryFn: () => fetchAdminPartners({ page: 1, limit: 200 }),
    enabled: typeOpen,
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => updateAdminClientStatus(id, status),
    onSuccess: () => {
      toast.success("Status updated");
      void qc.invalidateQueries({ queryKey: ["admin-client", id] });
      void qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const typeMutation = useMutation({
    mutationFn: () =>
      updateAdminClientType(id, {
        clientType: typeForm.clientType,
        controllingPartnerId:
          typeForm.clientType === "partner" ? typeForm.controllingPartnerId : null,
        reason: typeForm.reason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("Client type updated");
      setTypeOpen(false);
      void qc.invalidateQueries({ queryKey: ["admin-client", id] });
      void qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const editMutation = useMutation({
    mutationFn: () =>
      updateAdminClientProfile(id, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        address: editForm.address.trim(),
        username: editForm.username.trim().toLowerCase(),
        email: editForm.email.trim(),
      }),
    onSuccess: () => {
      toast.success("Client updated");
      setEditOpen(false);
      void qc.invalidateQueries({ queryKey: ["admin-client", id] });
      void qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (e: Error) => toast.error(friendlyClientError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminClient(id),
    onSuccess: () => {
      toast.success("Client deleted — status moved to cancelled");
      setDeleteOpen(false);
      void qc.invalidateQueries({ queryKey: ["admin-clients"] });
      navigate(ROUTES.CLIENTS);
    },
    onError: (e: Error) => toast.error(friendlyClientError(e)),
  });

  const client = detail.data?.client;

  if (detail.isLoading) {
    return <div className="h-40 rounded-2xl skeleton" />;
  }

  if (!client) {
    return (
      <div className="menu-page">
        <p className="text-[var(--text-muted)]">Client not found.</p>
        <button className="btn-ghost mt-3" onClick={() => navigate(ROUTES.CLIENTS)}>
          Back
        </button>
      </div>
    );
  }

  const isSuspended = client.status === "suspended";
  const storedType = (client.client_type as string) || "oelox";
  const typeStatus = String(client.client_type_status || "confirmed");
  // Partner-managed clients bill through the consolidated partner wallet —
  // the client wallet card stays read-only in that case.
  const isPartnerManaged = storedType === "partner" || !!client.controlling_partner_id;

  const billing = detail.data?.billing;
  const payerIsPartner = (billing?.payer ?? (isPartnerManaged ? "partner" : "client")) === "partner";
  const perTripFee = billing?.per_trip_fee_iqd ?? null;
  const walletBalance = Number(billing?.wallet?.balance ?? client.wallet_balance ?? 0);
  const partnerLinkCount = detail.data?.partnerLinks?.length ?? 0;

  const tabCounts: Partial<Record<TabKey, number>> = {
    orders: Number(client.order_count ?? 0),
    governance: partnerLinkCount,
  };

  return (
    <div className="menu-page">
      <header className="menu-page__header">
        <div className="min-w-0">
          <button className="btn-ghost mb-2" onClick={() => navigate(ROUTES.CLIENTS)}>
            <ArrowLeft className="w-4 h-4" /> All clients
          </button>
          <p className="menu-page__eyebrow">Client</p>
          <h1 className="menu-page__title">{String(client.name)}</h1>
          <p className="menu-page__subtitle flex items-center gap-2 flex-wrap">
            <span className={statusPillClass(String(client.status))}>{String(client.status)}</span>
            <span className={statusPillClass(storedType === "partner" ? "online" : "offline")}>
              {clientTypeLabel(storedType)}
            </span>
            {typeStatus !== "confirmed" ? (
              <span className={statusPillClass("trial")}>{clientTypeStatusLabel(typeStatus)}</span>
            ) : null}
            <span>{String(client.slug)}</span>
          </p>
        </div>
        <div className="menu-page__header-actions flex flex-wrap gap-2">
          <Button
            variant="secondary"
            className="min-h-[40px]"
            icon={<Pencil className="w-4 h-4" />}
            onClick={() => {
              setEditForm({
                name: String(client.name || ""),
                phone: String(client.phone || ""),
                address: String((client as Record<string, unknown>).address || ""),
                username: String(client.username || ""),
                email: String(client.owner_email || ""),
              });
              setEditOpen(true);
            }}
          >
            Edit client
          </Button>
          <Button
            variant="secondary"
            className="min-h-[40px]"
            onClick={() => {
              setTypeForm({
                clientType: storedType === "partner" ? "partner" : "oelox",
                controllingPartnerId: String(client.controlling_partner_id || ""),
                reason: "",
              });
              setTypeOpen(true);
            }}
          >
            Set client type
          </Button>
          {isSuspended ? (
            <Button
              className="min-h-[40px]"
              icon={<CheckCircle2 className="w-4 h-4" />}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate("active")}
            >
              Activate
            </Button>
          ) : (
            <Button
              variant="danger"
              className="min-h-[40px]"
              icon={<Ban className="w-4 h-4" />}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate("suspended")}
            >
              Suspend
            </Button>
          )}
          <Button
            variant="danger"
            className="min-h-[40px]"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </div>
      </header>

      <nav className="sa-tabbar" role="tablist" aria-label="Client sections">
        {TABS.map((t) => {
          const count = tabCounts[t.key];
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`sa-tabbar__tab ${tab === t.key ? "sa-tabbar__tab--active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {count ? <span className="sa-tabbar__count">{count}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "overview" ? (
        <div className="space-y-4">
          <StatsStrip
            tiles={[
              {
                key: "balance",
                label: "Wallet balance",
                value: formatIqd(walletBalance),
                icon: Wallet,
                tone: "accent",
                caption: payerIsPartner ? "Consolidated partner wallet" : "Client wallet",
              },
              {
                key: "trips",
                label: "Completed trips",
                value: formatNumber(Number(client.completed_trips ?? 0)),
                icon: Route,
                tone: "info",
                caption: "Driver-handled deliveries",
              },
              {
                key: "orders",
                label: "Orders",
                value: formatNumber(Number(client.order_count)),
                icon: ShoppingBag,
                tone: "ink",
                caption: "All non-cancelled",
              },
              {
                key: "customers",
                label: "Customers",
                value: formatNumber(Number(client.customer_count)),
                icon: Users,
                tone: "success",
                caption: "Registered at this business",
              },
            ]}
          />
          <section className="overview-panel">
            <header className="overview-panel__header">
              <div>
                <h3 className="overview-panel__title">Details</h3>
                <p className="overview-panel__subtitle">Client business profile</p>
              </div>
            </header>
            <div className="overview-panel__body p-4">
              <dl className="grid grid-cols-1 gap-x-8 text-sm sm:grid-cols-2">
                <DetailRow label="Client type (SoT)" value={clientTypeLabel(storedType)} />
                <DetailRow label="Type status" value={clientTypeStatusLabel(typeStatus)} />
                <DetailRow
                  label="Controlling partner"
                  value={String(client.controlling_partner_name || "—")}
                />
                <DetailRow label="Partner links (derived)" value={String(client.partner_names || "None")} />
                <DetailRow label="Phone" value={String(client.phone || "—")} />
                <DetailRow
                  label="Username (login ID)"
                  value={client.username ? `@${String(client.username)}` : "—"}
                />
                <DetailRow label="Email (invoices)" value={String(client.owner_email || "—")} />
                <DetailRow label="Currency" value={String(client.currency || "IQD")} />
                <DetailRow label="Created" value={formatDate(String(client.created_at))} />
              </dl>
            </div>
          </section>
        </div>
      ) : null}

      {tab === "billing" ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <section className="overview-panel xl:col-span-2">
              <header className="overview-panel__header">
                <div>
                  <h3 className="overview-panel__title">Per-trip billing</h3>
                  <p className="overview-panel__subtitle">
                    One fee per completed driver trip — no plans, no credit packages
                  </p>
                </div>
              </header>
              <div className="overview-panel__body p-4">
                <dl className="grid grid-cols-1 gap-x-8 text-sm sm:grid-cols-2">
                  <DetailRow
                    label="Payer"
                    value={
                      payerIsPartner
                        ? `Consolidated partner wallet${client.controlling_partner_name ? ` — ${String(client.controlling_partner_name)}` : ""}`
                        : "OELOX direct — client wallet"
                    }
                  />
                  <DetailRow
                    label="Per-trip fee"
                    value={perTripFee == null ? "Not configured" : formatIqd(perTripFee)}
                  />
                  <DetailRow
                    label={`Wallet balance${payerIsPartner ? " (partner)" : ""}`}
                    value={billing?.wallet ? formatIqd(Number(billing.wallet.balance)) : "—"}
                  />
                  <DetailRow
                    label="Negative credit limit"
                    value={
                      billing?.wallet
                        ? formatIqd(Number(billing.wallet.negative_credit_limit))
                        : "—"
                    }
                  />
                  <DetailRow
                    label="Low-balance alert"
                    value={
                      billing?.wallet
                        ? formatIqd(Number(billing.wallet.alert_threshold_iqd))
                        : "—"
                    }
                  />
                </dl>
                <p className="mt-4 text-xs text-[var(--text-muted)]">
                  {payerIsPartner
                    ? "This client is billed through the controlling partner's consolidated wallet. Limits and top-ups are managed on the partner page."
                    : "Fees deduct from this client's wallet per completed driver trip. Manage wallet limits and top-ups below. Reassigning a driver re-prices the trip automatically."}
                </p>
              </div>
            </section>
            <section className="overview-panel">
              <header className="overview-panel__header">
                <div>
                  <h3 className="overview-panel__title">How billing works</h3>
                  <p className="overview-panel__subtitle">Fee engine v3</p>
                </div>
              </header>
              <div className="overview-panel__body p-4">
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li>• Charged once per completed trip with an assigned driver.</li>
                  <li>• Fee source: Settings → Plans Settings (per-trip fee).</li>
                  <li>• Direct clients: client wallet is deducted per trip.</li>
                  <li>• Partner clients: the partner's consolidated wallet is deducted.</li>
                  <li>• If the configured fee is unavailable, the platform fallback applies and is logged.</li>
                </ul>
              </div>
            </section>
          </div>
          <AdminWalletCard
            walletType="client"
            ownerId={id}
            title="Client wallet (OELOX fees)"
            managedByPartner={isPartnerManaged}
          />
        </div>
      ) : null}

      {tab === "orders" ? (
        <section className="overview-panel">
          <header className="overview-panel__header">
            <div>
              <h3 className="overview-panel__title">Recent orders</h3>
              <p className="overview-panel__subtitle">Latest activity (max 25)</p>
            </div>
          </header>
          <div className="overview-panel__body">
            <div className="admin-table-wrap max-h-96 overflow-auto">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Customer</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {((detail.data?.recentOrders as Array<Record<string, unknown>>) ?? []).length ===
                  0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-[var(--text-muted)] py-6">
                        No orders yet
                      </td>
                    </tr>
                  ) : (
                    (detail.data?.recentOrders as Array<Record<string, unknown>>).map((o) => (
                      <tr key={String(o.id)}>
                        <td>{formatDate(String(o.created_at))}</td>
                        <td>{String(o.customer_name || "—")}</td>
                        <td className="text-[var(--text-muted)]">{String(o.channel || "—")}</td>
                        <td>
                          <span className={statusPillClass(String(o.status))}>
                            {String(o.status)}
                          </span>
                        </td>
                        <td className="tabular font-bold">{formatNumber(Number(o.total))}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {tab === "governance" ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <section className="overview-panel">
            <header className="overview-panel__header">
              <div>
                <h3 className="overview-panel__title">Partner links</h3>
                <p className="overview-panel__subtitle">Derived — never override client type</p>
              </div>
            </header>
            <div className="overview-panel__body p-4">
              {partnerLinkCount === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">No partner links.</p>
              ) : (
                <div className="space-y-2">
                  {detail.data?.partnerLinks?.map((link) => (
                    <div
                      key={String(link.partner_id)}
                      className="rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm"
                    >
                      <p className="font-bold">{String(link.partner_name)}</p>
                      <p className="text-[var(--text-muted)]">
                        {String(link.status)} · dispatch: {String(link.dispatch_authority)} · linked
                        by {String(link.linked_by)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
          <section className="overview-panel">
            <header className="overview-panel__header">
              <div>
                <h3 className="overview-panel__title">Client type audit</h3>
                <p className="overview-panel__subtitle">Every type decision, with reason</p>
              </div>
            </header>
            <div className="overview-panel__body p-4">
              {(detail.data?.clientTypeAudit?.length ?? 0) === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">No type decisions recorded.</p>
              ) : (
                <div className="space-y-2">
                  {detail.data?.clientTypeAudit?.slice(0, 8).map((row) => (
                    <div
                      key={String(row.id)}
                      className="rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm"
                    >
                      <p className="font-bold">
                        {String(row.action)} → {clientTypeLabel(row.new_client_type)} (
                        {clientTypeStatusLabel(row.new_status)})
                      </p>
                      <p className="text-[var(--text-muted)]">{String(row.decision_reason)}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {String(row.decision_code)} · {formatDate(String(row.created_at))}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}

      <FormModal
        open={typeOpen}
        onClose={() => setTypeOpen(false)}
        title="Set client type"
        subtitle="tenants.client_type is the single source of truth. Partner links do not override this."
        eyebrow="SA-1"
        icon={<CheckCircle2 className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setTypeOpen(false)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={
                typeMutation.isPending ||
                (typeForm.clientType === "partner" && !typeForm.controllingPartnerId)
              }
              onClick={() => typeMutation.mutate()}
            >
              {typeMutation.isPending ? "Saving…" : "Save type"}
            </button>
          </div>
        }
      >
        <div className="space-y-4 p-1">
          <label className="block space-y-1">
            <span className="text-sm font-medium">Client type</span>
            <select
              className="input-base"
              value={typeForm.clientType}
              onChange={(e) =>
                setTypeForm((f) => ({
                  ...f,
                  clientType: e.target.value === "partner" ? "partner" : "oelox",
                }))
              }
            >
              <option value="oelox">OELOX Client</option>
              <option value="partner">Partner Client</option>
            </select>
          </label>
          {typeForm.clientType === "partner" ? (
            <label className="block space-y-1">
              <span className="text-sm font-medium">Controlling partner</span>
              <select
                className="input-base"
                value={typeForm.controllingPartnerId}
                onChange={(e) =>
                  setTypeForm((f) => ({ ...f, controllingPartnerId: e.target.value }))
                }
              >
                <option value="">Select partner…</option>
                {(partnersQuery.data?.items ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="block space-y-1">
            <span className="text-sm font-medium">Reason</span>
            <input
              className="input-base"
              value={typeForm.reason}
              onChange={(e) => setTypeForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Why is this override needed?"
            />
          </label>
        </div>
      </FormModal>

      <FormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit client"
        subtitle="Username is the client's login ID. Email is optional — used later for invoices."
        eyebrow="Clients"
        icon={<Pencil className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setEditOpen(false)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={
                editMutation.isPending ||
                !editForm.name.trim() ||
                !editForm.phone.trim() ||
                !editForm.address.trim() ||
                !editForm.username.trim()
              }
              onClick={() => editMutation.mutate()}
            >
              {editMutation.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Business name
            <input
              className="input-base mt-1"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Phone
            <input
              className="input-base mt-1"
              value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Address
            <input
              className="input-base mt-1"
              value={editForm.address}
              onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Username (login ID)
            <input
              className="input-base mt-1"
              value={editForm.username}
              onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))}
              autoCapitalize="none"
              spellCheck={false}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Email (optional — invoices)
            <input
              className="input-base mt-1"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
        </div>
      </FormModal>

      <FormModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete client?"
        subtitle="This soft-deletes the client: status becomes cancelled and any active setup passcodes are revoked. The record and history stay in the system."
        eyebrow="Danger"
        icon={<Trash2 className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setDeleteOpen(false)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete client"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-[var(--text-secondary)]">
          The client signs in with their username. Setup passcodes are revoked immediately; the
          tenant and its history are preserved.
        </p>
      </FormModal>
    </div>
  );
}
